import { useEffect, useState } from "react";
import { Sparkles, RotateCw, Zap } from "lucide-react";
import api from "../lib/api";

export default function BriefingCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    try {
      const { data: d } = await api.get(
        `/admin/briefing${refresh ? "?refresh=true" : ""}`,
      );
      setData(d);
    } catch {
      setData({ bullets: ["Briefing unavailable right now."], source: "error" });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <section
        className="bg-white border border-jow-gold/60 p-5 animate-pulse"
        data-testid="briefing-loading"
      >
        <div className="h-3 w-32 bg-jow-navy/10 mb-3" />
        <div className="h-2 w-full bg-jow-navy/5 mb-2" />
        <div className="h-2 w-3/4 bg-jow-navy/5 mb-2" />
        <div className="h-2 w-5/6 bg-jow-navy/5" />
      </section>
    );
  }

  const bullets = data?.bullets || [];
  const source = data?.source;

  return (
    <section
      className="bg-white border border-jow-gold/60 p-5 relative overflow-hidden"
      data-testid="briefing-card"
    >
      <div className="absolute -top-6 -right-6 w-32 h-32 bg-jow-gold/10 rounded-full pointer-events-none" />
      <div className="relative flex items-start gap-4">
        <div className="w-10 h-10 bg-jow-navy text-jow-gold flex items-center justify-center shrink-0">
          <Sparkles size={18} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <div>
              <span className="label-overline text-jow-gold">Today's briefing</span>
              <p className="font-display text-lg text-jow-navy leading-tight mt-0.5">
                What's worth your attention today.
              </p>
            </div>
            <button
              onClick={() => load(true)}
              disabled={refreshing}
              className="text-[10px] uppercase tracking-widest text-jow-pink inline-flex items-center gap-1 hover:text-jow-navy disabled:opacity-40"
              data-testid="briefing-refresh"
            >
              <RotateCw size={12} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Refreshing" : "Refresh"}
            </button>
          </div>
          <ol className="space-y-2 mt-3">
            {bullets.map((b, i) => (
              <li
                key={i}
                className="flex gap-2 text-sm text-jow-navy leading-relaxed"
                data-testid={`briefing-bullet-${i}`}
              >
                <span className="font-display text-jow-gold shrink-0">
                  0{i + 1}
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ol>
          <p className="text-[9px] text-jow-navy/45 uppercase tracking-widest mt-4 flex items-center gap-1">
            <Zap size={10} className="text-jow-gold" />
            {source === "ai"
              ? "Powered by AI · claude-sonnet"
              : source === "rules"
              ? "Generated from your live data"
              : "Briefing unavailable"}
            {data?.cached && " · cached"}
          </p>
        </div>
      </div>
    </section>
  );
}
